import React, { createContext, useContext, useEffect, useRef, useState, ReactNode, useCallback } from 'react';
import { useNetworkStatus, checkNetworkStatus } from '@/hooks/useNetworkStatus';
import {
  getOfflineQueue,
  removeFromOfflineQueue,
  incrementOfflineOperationRetry,
  OfflineOperation,
  hasOfflineOperations,
  registerOfflineIdMapping,
  resolveOfflineId,
  removeChainedOfflineOperations,
  addToOfflineQueue,
} from '@/lib/offlineStorage';
import {
  cacheCatalog,
  cacheClients,
  cacheQuotes,
  getCachedCatalog,
  getCachedClients,
  getCachedQuotes,
  cacheFinanceEntries,
  getCachedFinanceEntries,
  cacheServiceOrders,
  getCachedServiceOrders,
  cacheAppointments,
  getCachedAppointments,
  cacheContracts,
  getCachedContracts,
  cacheReceipts,
  getCachedReceipts,
} from '@/lib/offlineDataCache';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { toast } from 'sonner';
import { ensureStorageUrl, isImageDataUrl, uploadImageDataUrl } from '@/lib/offlineSync/storageUpload';
import {
  FINANCE_ATTACHMENT_BUCKET,
  FINANCE_ATTACHMENT_FOLDER,
} from '@/hooks/useFinanceAttachment';
import { deleteProductsWithVariants } from '@/lib/productMutations';
import { prepareDocumentStockReturn } from '@/lib/stockDocumentReturn';
import { runDocumentDeletion } from '@/lib/documentDeletion';
import { deleteQuotesCascade } from '@/lib/quoteDeletion';
import { clearUserData, deleteCompaniesCascade } from '@/lib/companyDeletion';
import { Sentry } from '@/lib/sentry';
import { shouldIncrementQuoteQuota } from '@/lib/quickSale';
import { quoteItemFinanceEntryId, quoteItemUnitCost } from '@/lib/quoteItems';
import { serviceOrderItemUnitCost } from '@/lib/serviceOrderItems';
import { toDiagnosisColumns } from '@/lib/serviceOrderDiagnosis';
import { CONTRACT_CLAUSE_COLUMNS } from '@/lib/contractClauses';
import { ASSET_COLUMNS, toAssetColumns } from '@/lib/serviceOrderAsset';
import {
  SERVICE_ORDER_HEADER_COLUMNS,
  buildServiceOrderHeaderColumns,
  buildServiceOrderSignatureColumns,
} from '@/lib/serviceOrderHeader';
import { buildQuoteTotalsColumns } from '@/lib/quoteTotals';
import { normalizePaymentMethods } from '@/lib/quotePaymentInfo';
import { normalizeQuotePdfTemplate } from '@/lib/quotePdfTemplate';
import { buildQuoteWarrantyColumns } from '@/lib/quoteWarranty';
import { buildQuoteHeadingColumns } from '@/lib/quoteHeading';
import { buildQuoteExtraFieldsColumns, quoteExtraFieldsFromRow } from '@/lib/quoteExtraFields';
import { buildReceiptItemRow } from '@/lib/receiptItems';
import { productUnitCost } from '@/lib/cost';
import { getQuotePeriodStart } from '@/lib/subscriptionLimits';
import { todayIsoDate } from '@/lib/financeStatus';

/**
 * Resolve a usable company_id for an offline-created row on sync.
 * Prefers the id carried by the queued op (stripping temp `offline_` ids),
 * then falls back to the user's oldest company. Team-account rows must land in
 * a company or they'd be invisible to the now company-scoped lists.
 */
async function resolveSyncCompanyId(companyId: string | null | undefined, userId: string): Promise<string | null> {
  let resolved: string | null = companyId ?? null;
  if (resolved && String(resolved).startsWith('offline_')) {
    resolved = null;
  }
  if (!resolved) {
    // Prefer a company the user OWNS (oldest first) — this matches the historical
    // behaviour and is always writable.
    const { data: fallbackCompany, error } = await supabase
      .from('companies')
      .select('id')
      .eq('user_id', userId)
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle();
    if (error) {
      console.error('[NetworkContext] Error fetching fallback company for sync:', error);
    }
    resolved = (fallbackCompany as any)?.id ?? null;
  }
  if (!resolved) {
    // Team-account safety net: the user may own no company but still be a
    // writing member of one (owner/admin/member). Inserts require a company_id
    // the user can write to, so fall back to such a membership. Without this,
    // legacy queued ops with a null company_id fail RLS on sync.
    const { data: membership, error: memberError } = await supabase
      .from('company_members')
      .select('company_id, created_at')
      .eq('user_id', userId)
      .in('role', ['owner', 'admin', 'member'])
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle();
    if (memberError) {
      console.error('[NetworkContext] Error fetching fallback membership for sync:', memberError);
    }
    resolved = (membership as any)?.company_id ?? null;
  }
  return resolved;
}

// Helper to convert raw item data to clean DB payload (only valid quote_items columns)
async function toDbQuoteItem(raw: any, quoteId: string, position: number, userId: string): Promise<Record<string, any>> {
  const itemType = raw?.item_type || raw?.itemType || 'service';
  const discountType = raw?.discount_type || raw?.discountType || 'value';
  const quantity = Number(raw?.quantity ?? 1);
  const unitPrice = Number(raw?.unit_price ?? raw?.unitPrice ?? 0);
  const discount = Number(raw?.discount ?? 0);
  const subtotal = quantity * unitPrice;
  const computedTotal = discountType === 'percent'
    ? subtotal * (1 - discount / 100)
    : subtotal - discount;

  let imageUrl: string | null = raw?.image_url ?? raw?.imageUrl ?? null;
  // Upload DataURL images to storage before inserting
  if (isImageDataUrl(imageUrl)) {
    try {
      const uploaded = await uploadImageDataUrl(imageUrl, 'quote-photos', 'items', userId);
      imageUrl = uploaded.url;
    } catch (e) {
      console.warn('[NetworkContext] Failed to upload item image, setting to null:', e);
      imageUrl = null;
    }
  }

  // As outras duas fotos do cartão (até 3 por item). Mesma regra da primeira:
  // uma foto tirada offline chega aqui como `data:` e precisa subir ANTES do
  // insert — sem isso o Postgres recusaria a string gigante ou, pior, ela
  // ficaria presa no item para sempre em vez de virar um objeto do bucket.
  const uploadItemPhoto = async (raw: unknown): Promise<string | null> => {
    let url: string | null = (raw as string) ?? null;
    if (isImageDataUrl(url)) {
      try {
        const uploaded = await uploadImageDataUrl(url, 'quote-photos', 'items', userId);
        url = uploaded.url;
      } catch (e) {
        console.warn('[NetworkContext] Failed to upload item image, setting to null:', e);
        url = null;
      }
    }
    return url;
  };
  const imageUrl2 = await uploadItemPhoto(raw?.image_url_2 ?? raw?.imageUrl2 ?? null);
  const imageUrl3 = await uploadItemPhoto(raw?.image_url_3 ?? raw?.imageUrl3 ?? null);

  const productIdRaw = raw?.product_id ?? raw?.productId ?? null;
  // If the item still references an offline_ product id, try to resolve it to
  // the real UUID via the offline id map (the catalog create should have run
  // before us thanks to sync priority). Fall back to null if unmapped.
  const productIdCleaned = (() => {
    if (!productIdRaw) return null;
    const str = String(productIdRaw);
    if (!str.startsWith('offline_')) return productIdRaw;
    return resolveOfflineId(str) ?? null;
  })();

  return {
    quote_id: quoteId,
    name: String(raw?.name ?? ''),
    description: raw?.description ?? null,
    quantity,
    unit_price: unitPrice,
    // Snapshot do custo, tolerante ao formato: a fila guarda `unit_cost`, mas um
    // item vindo direto do formulário pode chegar em camelCase. Nunca relemos
    // `products.cost` aqui — na hora do sync o custo pode já ter mudado, e a
    // linha tem que preservar o custo do momento da venda.
    unit_cost: quoteItemUnitCost({ unit_cost: raw?.unit_cost ?? raw?.unitCost ?? null }),
    discount,
    discount_type: discountType,
    item_type: itemType,
    unit: raw?.unit ?? null,
    image_url: imageUrl,
    image_url_2: imageUrl2,
    image_url_3: imageUrl3,
    total: Number.isFinite(raw?.total) ? raw.total : Math.max(0, computedTotal),
    position,
    product_id: productIdCleaned,
    barcode: raw?.barcode ?? null,
    // Vínculo com a despesa cobrável. `quoteItemFinanceEntryId` descarta um id
    // `offline_`: vincular é online-only (a fila mapeia 1 operação → 1 uuid e
    // não teria como resolver o lançamento), então a linha degrada para comum
    // em vez de estourar `invalid input syntax for type uuid` no sync.
    finance_entry_id: quoteItemFinanceEntryId({ finance_entry_id: raw?.finance_entry_id ?? null }),
  };
}

type QuotePhotoInsert = Database['public']['Tables']['quote_photos']['Insert'];
type QuoteItemInsert = Database['public']['Tables']['quote_items']['Insert'];
type ServiceOrderItemInsert = Database['public']['Tables']['service_order_items']['Insert'];
type ServiceOrderPhotoInsert = Database['public']['Tables']['service_order_photos']['Insert'];

// Helper to convert raw photo data to clean DB payload (only valid quote_photos columns)
export async function toDbQuotePhoto(raw: any, quoteId: string, userId: string, position: number): Promise<QuotePhotoInsert | null> {
  const rawUrl = raw?.imageUrl || raw?.image_url;
  if (!rawUrl) return null;

  let finalUrl = rawUrl as string;
  if (isImageDataUrl(finalUrl)) {
    try {
      const uploaded = await uploadImageDataUrl(finalUrl, 'quote-photos', 'general', userId);
      finalUrl = uploaded.url;
    } catch (e) {
      console.warn('[NetworkContext] Failed to upload photo, skipping:', e);
      return null;
    }
  }

  return {
    quote_id: quoteId,
    user_id: userId,
    image_url: finalUrl,
    title: raw?.title || null,
    description: raw?.description || null,
    position,
  };
}

// Convert raw item data to a clean service_order_items payload (valid columns
// only; upload DataURL images to storage first). Mirrors toDbQuoteItem.
async function toDbServiceOrderItem(raw: any, serviceOrderId: string, position: number, userId: string): Promise<Record<string, any>> {
  const itemType = raw?.item_type || raw?.itemType || 'service';
  const discountType = raw?.discount_type || raw?.discountType || 'value';
  const quantity = Number(raw?.quantity ?? 1);
  const unitPrice = Number(raw?.unit_price ?? raw?.unitPrice ?? 0);
  const discount = Number(raw?.discount ?? 0);
  const subtotal = quantity * unitPrice;
  const computedTotal = discountType === 'percent'
    ? subtotal * (1 - discount / 100)
    : subtotal - discount;

  let imageUrl: string | null = raw?.image_url ?? raw?.imageUrl ?? null;
  if (isImageDataUrl(imageUrl)) {
    try {
      const uploaded = await uploadImageDataUrl(imageUrl, 'quote-photos', 'service-orders', userId);
      imageUrl = uploaded.url;
    } catch (e) {
      console.warn('[NetworkContext] Failed to upload SO item image, setting to null:', e);
      imageUrl = null;
    }
  }

  const productIdRaw = raw?.product_id ?? raw?.productId ?? null;
  const productIdCleaned = (() => {
    if (!productIdRaw) return null;
    const str = String(productIdRaw);
    if (!str.startsWith('offline_')) return productIdRaw;
    return resolveOfflineId(str) ?? null;
  })();

  return {
    service_order_id: serviceOrderId,
    name: String(raw?.name ?? ''),
    description: raw?.description ?? null,
    quantity,
    unit_price: unitPrice,
    // Snapshot do custo da OS (DECISÃO 2 do grupo 5), mesmo contrato do
    // orçamento: o custo vem DA FILA, nunca de uma leitura nova do catálogo —
    // quando a rede volta, `products.cost` pode já ter mudado.
    unit_cost: serviceOrderItemUnitCost({ unit_cost: raw?.unit_cost ?? raw?.unitCost ?? null }),
    discount,
    discount_type: discountType,
    item_type: itemType,
    unit: raw?.unit ?? null,
    image_url: imageUrl,
    total: Number.isFinite(raw?.total) ? raw.total : Math.max(0, computedTotal),
    position,
    product_id: productIdCleaned,
    barcode: raw?.barcode ?? null,
    // Origem do kit (grupo 10 / B1). O vínculo de kit é online-only, então o
    // pai é sempre um id real; um `offline_` aqui seria dado corrompido e vira
    // null, exatamente como `product_id` acima.
    bundle_parent_product_id: (() => {
      const parent = raw?.bundle_parent_product_id ?? raw?.bundleParentProductId ?? null;
      if (!parent) return null;
      const str = String(parent);
      return str.startsWith('offline_') ? (resolveOfflineId(str) ?? null) : parent;
    })(),
  };
}

// Convert raw photo data to a clean service_order_photos payload. Mirrors
// toDbQuotePhoto. Returns null when there's no usable image.
async function toDbServiceOrderPhoto(raw: any, serviceOrderId: string, userId: string, position: number): Promise<Record<string, any> | null> {
  const rawUrl = raw?.imageUrl || raw?.image_url;
  if (!rawUrl) return null;

  let finalUrl = rawUrl as string;
  if (isImageDataUrl(finalUrl)) {
    try {
      const uploaded = await uploadImageDataUrl(finalUrl, 'quote-photos', 'service-orders', userId);
      finalUrl = uploaded.url;
    } catch (e) {
      console.warn('[NetworkContext] Failed to upload SO photo, skipping:', e);
      return null;
    }
  }

  return {
    service_order_id: serviceOrderId,
    user_id: userId,
    image_url: finalUrl,
    title: raw?.title || null,
    description: raw?.description || null,
    position,
  };
}

export function describeOfflineOperation(operation: OfflineOperation): string {
  const quoteLabel = operation.data?.quote?.title || operation.data?.quote?.client_name;
  const recordLabel =
    operation.data?.client?.name ||
    operation.data?.item?.name ||
    operation.data?.entry?.description ||
    operation.data?.serviceOrder?.title ||
    operation.data?.serviceOrder?.client_name ||
    operation.data?.appointment?.title ||
    operation.data?.contract?.title ||
    operation.data?.contract?.client_name ||
    operation.data?.receipt?.payer_name ||
    operation.data?.receipt?.description ||
    quoteLabel;
  const trimmedLabel = recordLabel ? String(recordLabel).trim() : '';
  const suffix = trimmedLabel ? ` \"${trimmedLabel}\"` : '';

  switch (operation.type) {
    case 'create_quote':
    case 'update_quote':
    case 'delete_quote':
      return `orçamento${suffix}`;
    case 'create_client':
    case 'delete_client':
      return `cliente${suffix}`;
    case 'create_catalog_item':
    case 'delete_catalog_item':
      return `item do catálogo${suffix}`;
    case 'create_company':
    case 'delete_company':
      return `empresa${suffix}`;
    case 'update_profile':
      return 'perfil';
    case 'create_finance_entry':
    case 'update_finance_entry':
    case 'delete_finance_entry':
      return `lançamento financeiro${suffix}`;
    case 'create_service_order':
    case 'update_service_order':
    case 'delete_service_order':
      return `ordem de serviço${suffix}`;
    case 'create_appointment':
    case 'update_appointment':
    case 'delete_appointment':
      return `compromisso${suffix}`;
    case 'create_contract':
    case 'update_contract':
    case 'delete_contract':
      return `contrato${suffix}`;
    case 'create_receipt':
    case 'update_receipt':
    case 'delete_receipt':
      return `recibo${suffix}`;
    case 'create_stock_movement':
      return 'movimento de estoque';
    case 'clear_data':
      return 'limpeza de dados';
    default:
      return 'operação offline';
  }
}

export function restoreOrphanedCachedQuoteOperations(userId: string): number {
  const queuedIds = new Set(getOfflineQueue().map((operation) => operation.id));
  const cachedQuotes = (getCachedQuotes(userId) || []) as any[];
  let restored = 0;

  for (const cachedQuote of cachedQuotes) {
    const offlineId = String(cachedQuote?.id || '');
    if (
      cachedQuote?._offline_pending !== true ||
      !offlineId.startsWith('offline_') ||
      queuedIds.has(offlineId) ||
      resolveOfflineId(offlineId)
    ) {
      continue;
    }

    const {
      id: _id,
      _offline_pending: _offlinePending,
      _offline_items: offlineItems,
      _offline_photos: offlinePhotos,
      user_id: _userId,
      created_at: _createdAt,
      updated_at: _updatedAt,
      ...quote
    } = cachedQuote;

    addToOfflineQueue(
      'create_quote',
      {
        quote,
        items: Array.isArray(offlineItems) ? offlineItems : [],
        generalPhotos: Array.isArray(offlinePhotos) ? offlinePhotos : [],
        userId,
        companyId: cachedQuote.company_id ?? null,
      },
      offlineId,
    );
    queuedIds.add(offlineId);
    restored++;
  }

  return restored;
}

interface NetworkContextType {
  isOnline: boolean;
  isSyncing: boolean;
  pendingOperations: number;
  syncNow: () => Promise<void>;
}

const NetworkContext = createContext<NetworkContextType | undefined>(undefined);

const MAX_RETRIES = 3;

// Rewrite any lingering offline_ ids in an op's payload to real UUIDs via the
// offline id map. Returns a shallow-cloned op — the original is left intact so
// retries and cache updates still see the original state.
function translateOfflineIds(op: OfflineOperation): OfflineOperation {
  const resolveIfOffline = (val: any): any => {
    if (!val) return val;
    const str = String(val);
    if (!str.startsWith('offline_')) return val;
    return resolveOfflineId(str) ?? val;
  };

  switch (op.type) {
    case 'update_quote': {
      const nextUpdates = op.data?.updates
        ? {
            ...op.data.updates,
            client_id: resolveIfOffline(op.data.updates.client_id),
            company_id: resolveIfOffline(op.data.updates.company_id),
          }
        : op.data?.updates;
      return {
        ...op,
        data: {
          ...op.data,
          quoteId: resolveIfOffline(op.data?.quoteId),
          updates: nextUpdates,
        },
      };
    }
    case 'delete_quote':
      return { ...op, data: { ...op.data, quoteId: resolveIfOffline(op.data?.quoteId) } };
    case 'delete_client':
      return { ...op, data: { ...op.data, clientId: resolveIfOffline(op.data?.clientId) } };
    case 'delete_catalog_item':
      return { ...op, data: { ...op.data, itemId: resolveIfOffline(op.data?.itemId) } };
    case 'delete_company':
      return { ...op, data: { ...op.data, companyId: resolveIfOffline(op.data?.companyId) } };
    case 'delete_finance_entry':
      return { ...op, data: { ...op.data, entryId: resolveIfOffline(op.data?.entryId) } };
    case 'update_finance_entry': {
      const nextUpdates = op.data?.updates
        ? {
            ...op.data.updates,
            client_id: resolveIfOffline(op.data.updates.client_id),
            company_id: resolveIfOffline(op.data.updates.company_id),
            quote_id: resolveIfOffline(op.data.updates.quote_id),
            category_id: resolveIfOffline(op.data.updates.category_id),
          }
        : op.data?.updates;
      return {
        ...op,
        data: { ...op.data, entryId: resolveIfOffline(op.data?.entryId), updates: nextUpdates },
      };
    }
    case 'create_finance_entry': {
      const nextEntry = op.data?.entry
        ? {
            ...op.data.entry,
            client_id: resolveIfOffline(op.data.entry.client_id),
            company_id: resolveIfOffline(op.data.entry.company_id),
            quote_id: resolveIfOffline(op.data.entry.quote_id),
            category_id: resolveIfOffline(op.data.entry.category_id),
            recurrence_parent_id: resolveIfOffline(op.data.entry.recurrence_parent_id),
          }
        : op.data?.entry;
      return { ...op, data: { ...op.data, entry: nextEntry } };
    }
    case 'create_quote': {
      // client_id / company_id may reference an offline_ record whose create
      // already ran earlier in this sync pass. Resolve so the quote lands with
      // the correct FK instead of null. toDbQuoteItem resolves product_ids.
      const nextQuote = op.data?.quote
        ? {
            ...op.data.quote,
            client_id: resolveIfOffline(op.data.quote.client_id),
            company_id: resolveIfOffline(op.data.quote.company_id),
          }
        : op.data?.quote;
      return {
        ...op,
        data: {
          ...op.data,
          quote: nextQuote,
          companyId: resolveIfOffline(op.data?.companyId),
        },
      };
    }
    case 'create_service_order': {
      // quote_id / client_id / company_id may reference an offline_ record whose
      // create already ran earlier in this pass. Resolve so the SO lands with the
      // right FKs. toDbServiceOrderItem resolves product_ids.
      const nextOrder = op.data?.serviceOrder
        ? {
            ...op.data.serviceOrder,
            quote_id: resolveIfOffline(op.data.serviceOrder.quote_id),
            client_id: resolveIfOffline(op.data.serviceOrder.client_id),
            company_id: resolveIfOffline(op.data.serviceOrder.company_id),
          }
        : op.data?.serviceOrder;
      return {
        ...op,
        data: {
          ...op.data,
          serviceOrder: nextOrder,
          companyId: resolveIfOffline(op.data?.companyId),
        },
      };
    }
    case 'update_service_order': {
      const nextUpdates = op.data?.updates
        ? {
            ...op.data.updates,
            quote_id: resolveIfOffline(op.data.updates.quote_id),
            client_id: resolveIfOffline(op.data.updates.client_id),
            company_id: resolveIfOffline(op.data.updates.company_id),
          }
        : op.data?.updates;
      return {
        ...op,
        data: { ...op.data, serviceOrderId: resolveIfOffline(op.data?.serviceOrderId), updates: nextUpdates },
      };
    }
    case 'delete_service_order':
      return { ...op, data: { ...op.data, serviceOrderId: resolveIfOffline(op.data?.serviceOrderId) } };
    case 'create_appointment': {
      // client_id / service_order_id / quote_id / company_id may reference an
      // offline_ record whose create already ran earlier in this pass. Resolve
      // so the appointment lands with the right FKs.
      const nextAppointment = op.data?.appointment
        ? {
            ...op.data.appointment,
            client_id: resolveIfOffline(op.data.appointment.client_id),
            service_order_id: resolveIfOffline(op.data.appointment.service_order_id),
            quote_id: resolveIfOffline(op.data.appointment.quote_id),
            company_id: resolveIfOffline(op.data.appointment.company_id),
          }
        : op.data?.appointment;
      return {
        ...op,
        data: {
          ...op.data,
          appointment: nextAppointment,
          companyId: resolveIfOffline(op.data?.companyId),
        },
      };
    }
    case 'update_appointment': {
      const nextUpdates = op.data?.updates
        ? {
            ...op.data.updates,
            client_id: resolveIfOffline(op.data.updates.client_id),
            service_order_id: resolveIfOffline(op.data.updates.service_order_id),
            quote_id: resolveIfOffline(op.data.updates.quote_id),
            company_id: resolveIfOffline(op.data.updates.company_id),
          }
        : op.data?.updates;
      return {
        ...op,
        data: { ...op.data, appointmentId: resolveIfOffline(op.data?.appointmentId), updates: nextUpdates },
      };
    }
    case 'delete_appointment':
      return { ...op, data: { ...op.data, appointmentId: resolveIfOffline(op.data?.appointmentId) } };
    case 'create_contract': {
      // quote_id / client_id / company_id may reference an offline_ record whose
      // create already ran earlier in this pass. Resolve so the contract lands
      // with the right FKs.
      const nextContract = op.data?.contract
        ? {
            ...op.data.contract,
            quote_id: resolveIfOffline(op.data.contract.quote_id),
            client_id: resolveIfOffline(op.data.contract.client_id),
            company_id: resolveIfOffline(op.data.contract.company_id),
          }
        : op.data?.contract;
      return {
        ...op,
        data: {
          ...op.data,
          contract: nextContract,
          companyId: resolveIfOffline(op.data?.companyId),
        },
      };
    }
    case 'update_contract': {
      const nextUpdates = op.data?.updates
        ? {
            ...op.data.updates,
            quote_id: resolveIfOffline(op.data.updates.quote_id),
            client_id: resolveIfOffline(op.data.updates.client_id),
            company_id: resolveIfOffline(op.data.updates.company_id),
          }
        : op.data?.updates;
      return {
        ...op,
        data: { ...op.data, contractId: resolveIfOffline(op.data?.contractId), updates: nextUpdates },
      };
    }
    case 'delete_contract':
      return { ...op, data: { ...op.data, contractId: resolveIfOffline(op.data?.contractId) } };
    case 'create_receipt': {
      // quote_id / service_order_id / client_id / finance_entry_id / company_id
      // may reference an offline_ record whose create already ran earlier in this
      // pass. Resolve so the receipt lands with the right FKs.
      const nextReceipt = op.data?.receipt
        ? {
            ...op.data.receipt,
            quote_id: resolveIfOffline(op.data.receipt.quote_id),
            service_order_id: resolveIfOffline(op.data.receipt.service_order_id),
            client_id: resolveIfOffline(op.data.receipt.client_id),
            finance_entry_id: resolveIfOffline(op.data.receipt.finance_entry_id),
            company_id: resolveIfOffline(op.data.receipt.company_id),
          }
        : op.data?.receipt;
      return {
        ...op,
        data: {
          ...op.data,
          receipt: nextReceipt,
          companyId: resolveIfOffline(op.data?.companyId),
        },
      };
    }
    case 'update_receipt': {
      const nextUpdates = op.data?.updates
        ? {
            ...op.data.updates,
            quote_id: resolveIfOffline(op.data.updates.quote_id),
            service_order_id: resolveIfOffline(op.data.updates.service_order_id),
            client_id: resolveIfOffline(op.data.updates.client_id),
            finance_entry_id: resolveIfOffline(op.data.updates.finance_entry_id),
            company_id: resolveIfOffline(op.data.updates.company_id),
          }
        : op.data?.updates;
      return {
        ...op,
        data: { ...op.data, receiptId: resolveIfOffline(op.data?.receiptId), updates: nextUpdates },
      };
    }
    case 'delete_receipt':
      return { ...op, data: { ...op.data, receiptId: resolveIfOffline(op.data?.receiptId) } };
    case 'create_stock_movement': {
      // product_id/quote_id/service_order_id/company_id podem referenciar um
      // registro offline_ cujo create já rodou nesta passada (produto=prio 20,
      // quote=40, OS=45 rodam antes do movimento=49). Resolve p/ a RPC receber
      // UUIDs reais.
      const nextMovement = op.data?.movement
        ? {
            ...op.data.movement,
            product_id: resolveIfOffline(op.data.movement.product_id),
            quote_id: resolveIfOffline(op.data.movement.quote_id),
            service_order_id: resolveIfOffline(op.data.movement.service_order_id),
            company_id: resolveIfOffline(op.data.movement.company_id),
          }
        : op.data?.movement;
      return {
        ...op,
        data: {
          ...op.data,
          movement: nextMovement,
          companyId: resolveIfOffline(op.data?.companyId),
        },
      };
    }
    default:
      return op;
  }
}

export function NetworkProvider({ children }: { children: ReactNode }) {
  const { isOnline } = useNetworkStatus();
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingOperations, setPendingOperations] = useState(0);

  // Prevent concurrent sync loops (can cause duplicate inserts)
  const syncLockRef = useRef(false);

  // When a sync finishes but there are still pending ops, retry once shortly after.
  // This is specifically to recover from transient reconnect flakiness without requiring
  // the user to toggle airplane mode again.
  const retryTimerRef = useRef<number | null>(null);

  // Update pending count
  const updatePendingCount = useCallback(() => {
    setPendingOperations(getOfflineQueue().length);
  }, []);

  // Sync a single operation
  const syncOperation = async (operation: OfflineOperation): Promise<boolean> => {
    try {
      switch (operation.type) {
        case 'create_quote': {
          const { quote, items, userId, companyId } = operation.data;
          const countTowardQuoteQuota = shouldIncrementQuoteQuota(operation.data);

          console.log('[NetworkContext] Starting quote sync, operation:', operation.id);

          let nextSequenceId: number | null = null;
          if (countTowardQuoteQuota) {
            // Orçamentos normais mantêm exatamente a numeração histórica.
            const [{ data: profileData, error: profileFetchError }, { data: maxSeqRow, error: seqFetchError }] = await Promise.all([
              supabase
                .from('profiles')
                .select('lifetime_quotes_created')
                .eq('id', userId)
                .maybeSingle(),
              supabase
                .from('quotes')
                .select('sequence_id')
                .eq('user_id', userId)
                .not('sequence_id', 'is', null)
                .order('sequence_id', { ascending: false })
                .limit(1)
                .maybeSingle(),
            ]);

            if (profileFetchError) {
              console.error('[NetworkContext] Error fetching profile for sequence_id:', profileFetchError);
            }
            if (seqFetchError) {
              console.error('[NetworkContext] Error fetching max sequence_id:', seqFetchError);
            }

            const profileCount = profileData?.lifetime_quotes_created ?? 0;
            const maxExistingSeq = (maxSeqRow as any)?.sequence_id ?? 0;
            nextSequenceId = Math.max(profileCount, maxExistingSeq) + 1;
          }
          
          // Clean up temporary offline IDs before inserting
          // If client_id starts with 'offline_', set to null (prevents FK error)
          const cleanedQuote = { ...quote };
          if (cleanedQuote.client_id && String(cleanedQuote.client_id).startsWith('offline_')) {
            cleanedQuote.client_id = null;
          }

          // Resolve a safe company_id for insert. Team-account RLS rejects any
          // quote whose company_id the user can't write to (null included), so
          // resolve to the carried company or a writable fallback.
          const resolvedCompanyId = await resolveSyncCompanyId(
            companyId ?? cleanedQuote.company_id,
            userId,
          );

          // If we still don't have a company, don't attempt insert (it will likely fail).
          if (!resolvedCompanyId) {
            console.error('[NetworkContext] Quote sync aborted: missing company_id');
            if ((operation as any)?.retries === 0) {
              toast.error('Não foi possível sincronizar o orçamento: selecione/crie uma empresa e tente novamente.');
            }
            return false;
          }

          // Build the final payload with only valid columns for quotes table
          const quotePayload = {
            user_id: userId,
            company_id: resolvedCompanyId,
            client_id: cleanedQuote.client_id || null,
            client_name: cleanedQuote.client_name || null,
            client_phone: cleanedQuote.client_phone || null,
            client_address: cleanedQuote.client_address || null,
            client_document: cleanedQuote.client_document || null,
            status: cleanedQuote.status || 'pending',
            document_type: cleanedQuote.document_type || 'orcamento',
            validity_days: cleanedQuote.validity_days || null,
            ...buildQuoteWarrantyColumns(cleanedQuote),
            // Cabeçalho (subtítulo + local de emissão): vêm PRONTOS da fila, em
            // nome de coluna. O executor não tem o formulário, só o payload.
            ...buildQuoteHeadingColumns({
              subtitle: cleanedQuote.subtitle ?? '',
              issuePlace: cleanedQuote.issue_place ?? '',
            }),
            ...buildQuoteExtraFieldsColumns({
              extraFields: quoteExtraFieldsFromRow(cleanedQuote),
            }),
            notes: cleanedQuote.notes || null,
            terms_conditions: cleanedQuote.terms_conditions || null,
            // Frete e desconto de cabeçalho vêm PRONTOS da fila (foram
            // calculados quando o usuário salvou, offline). Reler/recalcular
            // aqui seria pior: o executor não tem o formulário, só o payload.
            total: Number(cleanedQuote.total ?? 0),
            shipping: Number(cleanedQuote.shipping ?? 0),
            discount: Number(cleanedQuote.discount ?? 0),
            discount_type: cleanedQuote.discount_type === 'percent' ? 'percent' : 'value',
            // Campos de exibição do grupo 4. `normalizePaymentMethods` preserva
            // a distinção `null` (legado → o PDF mantém o QR PIX) × `[]` (o
            // usuário desmarcou tudo). Um `?? []` aqui apagaria o QR de todo
            // orçamento criado offline sem tocar no campo.
            payment_methods: normalizePaymentMethods(cleanedQuote.payment_methods),
            payment_terms: cleanedQuote.payment_terms || null,
            reference: cleanedQuote.reference || null,
            // Modelo do PDF escolhido no formulário, já em nome de coluna
            // (persistQuoteOffline gravou via builder). null = padrão da empresa.
            pdf_template: normalizeQuotePdfTemplate(cleanedQuote.pdf_template),
            title: cleanedQuote.title || null,
            show_sequence_id: cleanedQuote.show_sequence_id || false,
            sequence_id: nextSequenceId,
          };

          console.log('[NetworkContext] Quote payload:', JSON.stringify(quotePayload));
          
          // Insert quote with sequence_id
          const { data: newQuote, error: quoteError } = await supabase
            .from('quotes')
            .insert(quotePayload)
            .select('*')
            .single();

          if (quoteError) {
            console.error('[NetworkContext] Quote insert error:', quoteError);
            throw quoteError;
          }

          console.log('[NetworkContext] Quote inserted successfully:', newQuote.id);

          // Insert items using sanitized payload (only valid DB columns, upload DataURLs)
          if (items && items.length > 0) {
            const itemsToInsert: Record<string, any>[] = [];
            for (let i = 0; i < items.length; i++) {
              itemsToInsert.push(await toDbQuoteItem(items[i], newQuote.id, i, userId));
            }

            const { error: itemsError } = await supabase
              .from('quote_items')
              .insert(itemsToInsert as QuoteItemInsert[]);

            if (itemsError) {
              console.error('[NetworkContext] Quote items insert error:', itemsError);
              // Best-effort rollback to prevent duplicates on retry
              await supabase.from('quote_items').delete().eq('quote_id', newQuote.id);
              await supabase.from('quotes').delete().eq('id', newQuote.id);
              throw itemsError;
            }
          }

          // Insert general photos using sanitized payload
          const generalPhotos = operation.data.generalPhotos || operation.data.general_photos || [];
          if (Array.isArray(generalPhotos) && generalPhotos.length > 0) {
            const photosToInsert: QuotePhotoInsert[] = [];
            for (let i = 0; i < generalPhotos.length; i++) {
              const row = await toDbQuotePhoto(generalPhotos[i], newQuote.id, userId, i);
              if (row) photosToInsert.push(row);
            }
            if (photosToInsert.length > 0) {
              const { error: photosError } = await supabase.from('quote_photos').insert(photosToInsert);
              if (photosError) {
                console.error('[NetworkContext] Quote photos insert error:', photosError);
                // Roll back to prevent partial state
                await supabase.from('quote_items').delete().eq('quote_id', newQuote.id);
                await supabase.from('quote_photos').delete().eq('quote_id', newQuote.id);
                await supabase.from('quotes').delete().eq('id', newQuote.id);
                throw photosError;
              }
            }
          }
          
          if (countTowardQuoteQuota) {
            const { error: profileUpdateError } = await supabase.rpc(
              'record_quote_creation',
              {
                p_period_start: getQuotePeriodStart(),
                p_count_toward_quota: true,
                p_sequence_id: nextSequenceId!,
              },
            );

            if (profileUpdateError) {
              // Rollback quote to avoid sequence number reuse in online flow
              await supabase.from('quote_items').delete().eq('quote_id', newQuote.id);
              await supabase.from('quotes').delete().eq('id', newQuote.id);
              throw profileUpdateError;
            }

            window.dispatchEvent(new CustomEvent('quote-quota-updated'));
          }

          // Replace temporary offline quote in cache with the real saved quote
          const cachedQuotes = (getCachedQuotes(userId) || []) as any[];
          // Filter out the temp offline quote AND any stale duplicate
          const filteredQuotes = cachedQuotes.filter((q: any) => q.id !== operation.id && q.id !== newQuote.id);
          filteredQuotes.unshift({
            ...(newQuote as any),
            _offline_pending: false,
            _offline_items: items || [],
            _offline_photos: operation.data.generalPhotos || [],
          });
          cacheQuotes(userId, filteredQuotes);

          registerOfflineIdMapping(operation.id, newQuote.id);

          console.log('[NetworkContext] Quote synced successfully:', newQuote.id, 'replacing temp:', operation.id);

          return true;
        }

        case 'create_client': {
          const { client, userId, companyId } = operation.data;

          const { data: authData } = await supabase.auth.getUser();
          const effectiveUserId = authData.user?.id || userId;

          const resolvedCompanyId = await resolveSyncCompanyId(companyId, effectiveUserId);

          const clientPayload = {
            name: String(client?.name || '').trim(),
            phone: client?.phone || null,
            address: client?.address || null,
            document: client?.document || null,
            email: client?.email || null,
            cep: client?.cep || null,
            logradouro: client?.logradouro || null,
            numero: client?.numero || null,
            complemento: client?.complemento || null,
            bairro: client?.bairro || null,
            municipio_nome: client?.municipio_nome || null,
            municipio_ibge: client?.municipio_ibge || null,
            uf: client?.uf || null,
            inscricao_estadual: client?.inscricao_estadual || null,
            // Aniversário e foto: payload de LISTA EXPLÍCITA, a armadilha
            // #33/#62 — sem estas duas linhas o cliente cadastrado SEM REDE
            // sobe sem elas quando a fila sincroniza, com sucesso e sem erro.
            birth_date: client?.birth_date || null,
            photo_url: client?.photo_url || null,
            user_id: effectiveUserId,
            company_id: resolvedCompanyId,
          };

          const { data: created, error } = await supabase
            .from('clients')
            .insert(clientPayload)
            .select('*');

          if (error) throw error;

          // Replace temp client in cache (if present)
          const cachedClients = (getCachedClients(effectiveUserId) || []) as any[];
          const filtered = cachedClients.filter((c: any) => c.id !== operation.id);
          if (created && created[0]) {
            filtered.unshift({ ...(created[0] as any), _offline_pending: false });
            registerOfflineIdMapping(operation.id, (created[0] as any).id);
          }
          cacheClients(effectiveUserId, filtered);

          return true;
        }

        case 'create_catalog_item': {
          const { item, userId, companyId } = operation.data;

          const { data: authData } = await supabase.auth.getUser();
          const effectiveUserId = authData.user?.id || userId;

          const resolvedCompanyId = await resolveSyncCompanyId(companyId, effectiveUserId);

          const queuedPhotos = Array.isArray(item?.product_photos)
            ? item.product_photos.slice(0, 3)
            : (item?.image_url || item?.imageUrl)
              ? [{ image_url: item.image_url || item.imageUrl, position: 0 }]
              : [];
          const uploadedPhotos: Array<{
            image_url: string;
            title: string | null;
            description: string | null;
            position: number;
          }> = [];
          for (let index = 0; index < queuedPhotos.length; index += 1) {
            const photo = queuedPhotos[index];
            let url = photo?.image_url ?? null;
            if (isImageDataUrl(url)) {
              const uploaded = await uploadImageDataUrl(
                url,
                'product-photos',
                'catalog',
                effectiveUserId,
                resolvedCompanyId ?? effectiveUserId,
              );
              url = uploaded.url;
            }
            if (!url) continue;
            uploadedPhotos.push({
              image_url: url,
              title: photo?.title ?? null,
              description: photo?.description ?? null,
              position: uploadedPhotos.length,
            });
          }
          const imageUrl = uploadedPhotos[0]?.image_url ?? null;

          // Variação: um pai que ainda seja `offline_` não existe no Postgres,
          // então a filha entraria como uuid inválido. Nesse caso a linha sobe
          // como produto simples (raiz) em vez de falhar o sync inteiro — o app
          // já impede criar variações offline, isto é só o cinto de segurança.
          const rawParentId = item?.parent_product_id ?? null;
          const parentProductId =
            rawParentId && !String(rawParentId).startsWith('offline_') ? String(rawParentId) : null;

          const productPayload = {
            user_id: effectiveUserId,
            company_id: resolvedCompanyId,
            parent_product_id: parentProductId,
            variant_name: parentProductId ? (item?.variant_name ?? null) : null,
            name: String(item?.name || '').trim(),
            description: item?.description || null,
            price: Number(item?.price ?? item?.unit_price ?? 0),
            // Custo: `null` quando não informado — `Number(...)` como no preço
            // acima transformaria a ausência em 0 ("de graça"), que é justamente
            // o que esta feature não pode fazer.
            cost: productUnitCost(item),
            type: item?.type === 'product' ? 'product' : 'service',
            unit: item?.unit || null,
            category: item?.category ?? null,
            barcode: item?.barcode || null,
            internal_code: item?.internal_code || null,
            lc116_item: item?.lc116_item ?? null,
            iss_aliquota: item?.iss_aliquota ?? null,
            iss_retido: item?.iss_retido === true,
            nbs: item?.nbs ?? null,
            cclasstrib: item?.cclasstrib ?? null,
            track_stock: item?.track_stock ?? false,
            min_stock: item?.min_stock ?? null,
            stock_deduction_mode:
              item?.stock_deduction_mode === 'off'
              || item?.stock_deduction_mode === 'ask'
              || item?.stock_deduction_mode === 'auto'
                ? item.stock_deduction_mode
                : null,
            image_url: imageUrl,
          };

          const { data: created, error } = await supabase
            .from('products')
            .insert(productPayload)
            .select('*');

          if (error) throw error;

          const createdProductId = created?.[0]?.id ? String(created[0].id) : null;
          if (createdProductId && uploadedPhotos.length > 0) {
            const photoRows = uploadedPhotos.map((photo) => ({
              product_id: createdProductId,
              company_id: resolvedCompanyId,
              user_id: effectiveUserId,
              image_url: photo.image_url,
              title: photo.title,
              description: photo.description,
              position: photo.position,
            }));
            const { error: photosError } = await supabase
              .from('product_photos')
              .insert(photoRows);
            if (photosError) {
              await supabase.from('products').delete().eq('id', createdProductId);
              throw photosError;
            }
          }

          // Replace temp item in cache (if present)
          const cachedCatalog = (getCachedCatalog(effectiveUserId) || []) as any[];
          const filtered = cachedCatalog.filter((p: any) => p.id !== operation.id);
          if (created && created[0]) {
            filtered.unshift({
              ...(created[0] as any),
              product_photos: uploadedPhotos,
              _offline_photos: uploadedPhotos,
              _offline_pending: false,
            });
            registerOfflineIdMapping(operation.id, (created[0] as any).id);
          }
          cacheCatalog(effectiveUserId, filtered);

          return true;
        }

        case 'update_quote': {
          const { quoteId, updates, items, generalPhotos } = operation.data;
          let { userId } = operation.data;

          // Older queued ops may not carry userId; resolve from current session as fallback.
          if (!userId) {
            const { data: authData } = await supabase.auth.getUser();
            userId = authData.user?.id ?? undefined;
          }

          // Update quote (sanitize updates to avoid type/column errors from UI payloads)
          const quoteUpdatePayload: Record<string, any> = {
            client_id: updates?.client_id ?? null,
            client_name: updates?.client_name ?? null,
            client_phone: updates?.client_phone ?? null,
            client_address: updates?.client_address ?? null,
            client_document: updates?.client_document ?? null,
            company_id: updates?.company_id ?? null,
            status: updates?.status ?? null,
            document_type: updates?.document_type ?? null,
            validity_days: updates?.validity_days ?? null,
            // Garantia: as TRÊS colunas de uma vez. Este payload é montado por
            // LISTA EXPLÍCITA e é o SEGUNDO de `quotes` neste arquivo (#89) —
            // gravar só `warranty` faria a edição OFFLINE subir sem a garantia
            // estruturada, com toast de sucesso e o campo voltando ao valor
            // anterior ao sincronizar.
            ...buildQuoteWarrantyColumns({
              warranty: updates?.warranty ?? '',
              warrantyAmount: updates?.warranty_amount ?? null,
              warrantyUnit: updates?.warranty_unit ?? null,
            }),
            // Bloco PRÓPRIO do `update_quote` — o guard cobra por BLOCO (#89),
            // porque este payload é montado campo a campo, separado do create.
            ...buildQuoteHeadingColumns({
              subtitle: updates?.subtitle ?? '',
              issuePlace: updates?.issue_place ?? '',
            }),
            ...buildQuoteExtraFieldsColumns({
              extraFields: quoteExtraFieldsFromRow(updates),
            }),
            notes: updates?.notes ?? null,
            terms_conditions: updates?.terms_conditions ?? null,
            total: updates?.total ?? null,
            shipping: updates?.shipping ?? 0,
            discount: updates?.discount ?? 0,
            discount_type: updates?.discount_type === 'percent' ? 'percent' : 'value',
            payment_methods: normalizePaymentMethods(updates?.payment_methods),
            payment_terms: updates?.payment_terms ?? null,
            reference: updates?.reference ?? null,
            // Modelo do PDF: SEGUNDO payload de `quotes` deste arquivo (#89) —
            // sem esta linha a edição offline subiria descartando a escolha.
            pdf_template: normalizeQuotePdfTemplate(updates?.pdf_template),
            title: updates?.title ?? null,
            show_sequence_id: updates?.show_sequence_id ?? null,
            updated_at: updates?.updated_at ?? new Date().toISOString(),
          };

          const { error: quoteError } = await supabase
            .from('quotes')
            .update(quoteUpdatePayload)
            .eq('id', quoteId);

          if (quoteError) throw quoteError;

          // Update items if provided (use sanitized payload)
          if (items) {
            if (!userId) {
              throw new Error('update_quote: userId is required to sync item images');
            }
            // Delete existing items
            await supabase.from('quote_items').delete().eq('quote_id', quoteId);

            // Insert new items using the sanitized helper
            if (items.length > 0) {
              const itemsToInsert: Record<string, any>[] = [];
              for (let i = 0; i < items.length; i++) {
                itemsToInsert.push(await toDbQuoteItem(items[i], quoteId, i, userId));
              }

              const { error: itemsError } = await supabase
                .from('quote_items')
                .insert(itemsToInsert as QuoteItemInsert[]);

              if (itemsError) throw itemsError;
            }
          }

          // Update general photos (IMPORTANT: do NOT delete existing photos first).
          // In offline editing of online quotes, it's common that we don't have the full
          // existing photo list available offline; deleting would cause permanent loss.
          if (Array.isArray(generalPhotos) && userId) {
            const newPhotos = generalPhotos.filter((p: any) => {
              const url = p?.imageUrl || p?.image_url;
              return isImageDataUrl(url);
            });

            if (newPhotos.length > 0) {
              const { data: lastPhoto } = await supabase
                .from('quote_photos')
                .select('position')
                .eq('quote_id', quoteId)
                .order('position', { ascending: false })
                .limit(1)
                .maybeSingle();

              const startPosition = Number((lastPhoto as any)?.position ?? -1) + 1;

              const photosToInsert: QuotePhotoInsert[] = [];
              for (let i = 0; i < newPhotos.length; i++) {
                const row = await toDbQuotePhoto(newPhotos[i], quoteId, userId, startPosition + i);
                if (row) photosToInsert.push(row);
              }

              if (photosToInsert.length > 0) {
                const { error: photosError } = await supabase.from('quote_photos').insert(photosToInsert);
                if (photosError) throw photosError;
              }
            }
          }

          // Update cache copy
          if (userId) {
            const cachedQuotes = (getCachedQuotes(userId) || []) as any[];
            const idx = cachedQuotes.findIndex((q: any) => q.id === quoteId);
            if (idx !== -1) {
              const prev = cachedQuotes[idx] || {};

              // Preserve any previously cached photos if the offline payload doesn't include them.
              const prevPhotos: any[] = Array.isArray(prev._offline_photos) ? prev._offline_photos : [];
              const nextPhotosRaw: any[] = Array.isArray(generalPhotos) ? generalPhotos : [];
              const mergedPhotos = (() => {
                const map = new Map<string, any>();
                for (const p of [...prevPhotos, ...nextPhotosRaw]) {
                  const key = p?.imageUrl || p?.image_url || p?.id;
                  if (!key) continue;
                  if (!map.has(key)) map.set(key, p);
                }
                return Array.from(map.values());
              })();

              cachedQuotes[idx] = {
                ...prev,
                ...quoteUpdatePayload,
                _offline_pending: false,
                _offline_items: items || cachedQuotes[idx]._offline_items,
                _offline_photos: mergedPhotos.length > 0 ? mergedPhotos : prevPhotos,
                updated_at: new Date().toISOString(),
              };
              cacheQuotes(userId, cachedQuotes);
            }
          }

          return true;
        }

        case 'delete_quote': {
          const { quoteId, userId, companyId, returnStock } = operation.data;

          // A devolução é CALCULADA AQUI, não no momento em que a exclusão foi
          // enfileirada: offline não há como ler os movimentos do servidor. O
          // que a fila carregou foi a DECISÃO. Calcular antes do delete (o
          // vínculo some com ele) e aplicar depois (não escrever no estoque de
          // um documento que pode não morrer) — ver documentDeletion.ts.
          //
          // Os filhos são CASCADE: apagá-los aqui só abriria a mesma janela de
          // corrupção que a tela do orçamento tinha (ver quoteDeletion.ts).
          await runDocumentDeletion({
            prepareReturn: () =>
              returnStock === true && companyId
                ? prepareDocumentStockReturn(String(companyId), 'quote', [String(quoteId)])
                : Promise.resolve(null),
            remove: () => deleteQuotesCascade([String(quoteId)]),
          });

          // Update cache
          const cached = (getCachedQuotes(userId) || []) as any[];
          cacheQuotes(userId, cached.filter((q: any) => q.id !== quoteId));

          return true;
        }

        case 'delete_client': {
          const { clientId, userId } = operation.data;

          await supabase.from('clients').delete().eq('id', clientId).eq('user_id', userId);
          const cached = (getCachedClients(userId) || []) as any[];
          cacheClients(userId, cached.filter((c: any) => c.id !== clientId));

          return true;
        }

        case 'delete_catalog_item': {
          const { itemId, userId } = operation.data;

          // Apaga o produto E suas variações, desvinculando antes os
          // `quote_items` de pai e filhas (a FK é NO ACTION — sem o unlink das
          // filhas o cascade estoura violação de chave estrangeira).
          const { error: deleteError, deletedIds } = await deleteProductsWithVariants([String(itemId)]);
          if (deleteError) throw deleteError;

          const removed = new Set(deletedIds.map(String));
          const cached = (getCachedCatalog(userId) || []) as any[];
          cacheCatalog(
            userId,
            cached.filter((p: any) => !removed.has(String(p.id)) && p.id !== itemId),
          );

          return true;
        }

        case 'clear_data': {
          // UMA TRANSAÇÃO, NO SERVIDOR (`src/lib/companyDeletion.ts`).
          //
          // Antes eram quatro deletes em sequência aqui, e o de `companies`
          // falhava com 23502 depois de orçamentos, clientes e catálogo já terem
          // sido destruídos — com o agravante de que, neste caminho, o
          // `return true` tiraria a operação da fila como CONCLUÍDA. Agora ou
          // tudo acontece, ou nada acontece, e um erro lança para a operação
          // ficar na fila. Aqui não há devolução de estoque: os produtos vão
          // junto (ver `src/lib/stockDocumentReturn.ts`).
          //
          // `includeCompanies` deixou de existir: a RPC apaga as empresas
          // sempre, porque é a queda delas que leva o resto por CASCADE — e a
          // tela nunca ofereceu o outro modo.
          await clearUserData();
          return true;
        }

        case 'create_company': {
          const companyData = { ...operation.data };

          if (isImageDataUrl(companyData.logo_url)) {
            const ownerId = companyData.user_id
              || (await supabase.auth.getUser()).data.user?.id;
            if (!ownerId) {
              console.warn('[NetworkContext] create_company: no user_id, skipping logo upload');
              companyData.logo_url = null;
            } else {
              try {
                const uploaded = await uploadImageDataUrl(companyData.logo_url, 'public-app-assets', 'company-logos', ownerId);
                companyData.logo_url = uploaded.url;
              } catch (e) {
                console.warn('[NetworkContext] Failed to upload company logo, saving without logo:', e);
                companyData.logo_url = null;
              }
            }
          }

          const { data: createdCompany, error } = await supabase
            .from('companies')
            .insert(companyData)
            .select('id')
            .single();

          if (error) throw error;

          if (createdCompany?.id) {
            registerOfflineIdMapping(operation.id, createdCompany.id as string);
          }
          return true;
        }

        case 'update_profile': {
          const { userId, updates } = operation.data;
          const patched = { ...updates };

          if (isImageDataUrl(patched.logo_url)) {
            const ownerId = userId || (await supabase.auth.getUser()).data.user?.id;
            if (!ownerId) {
              console.warn('[NetworkContext] update_profile: no userId, dropping logo from update');
              delete patched.logo_url;
            } else {
              try {
                const uploaded = await uploadImageDataUrl(patched.logo_url, 'public-app-assets', 'company-logos', ownerId);
                patched.logo_url = uploaded.url;
              } catch (e) {
                console.warn('[NetworkContext] Failed to upload profile logo, dropping logo from update:', e);
                delete patched.logo_url;
              }
            }
          }

          const { error } = await supabase
            .from('profiles')
            .update(patched)
            .eq('id', userId);

          if (error) throw error;
          return true;
        }

        case 'delete_company': {
          const { companyId } = operation.data;

          // PELA PORTA ÚNICA (`src/lib/companyDeletion.ts`). O delete cru daqui
          // falhava com 23502 enquanto `company_id` era NOT NULL com FK SET NULL,
          // e mesmo com o CASCADE ainda pode ser recusado com 23514 quando uma
          // despesa cobrável aponta para um orçamento desta empresa. Um erro de
          // verdade lança e a operação FICA na fila para a próxima tentativa.
          const { deleted } = await deleteCompaniesCascade([companyId]);

          // Zero é SUCESSO aqui, ao contrário do caminho online (onde vira
          // aviso na tela): a empresa já não existe — apagada em outro
          // dispositivo, ou esta operação já rodou. Lançar a manteria na fila
          // para sempre, travando as operações dos outros módulos atrás dela.
          if (deleted === 0) {
            console.warn('[NetworkContext] delete_company: nada a apagar para', companyId);
          }
          return true;
        }

        case 'create_finance_entry': {
          const { entry, userId } = operation.data;

          const { data: authData } = await supabase.auth.getUser();
          const effectiveUserId = authData.user?.id || userId;

          // Strip any lingering offline_ FKs so we never hand Postgres a temp id.
          const nullIfOffline = (val: any): string | null => {
            if (!val) return null;
            const str = String(val);
            return str.startsWith('offline_') ? null : str;
          };

          const resolvedCompanyId = await resolveSyncCompanyId(nullIfOffline(entry?.company_id), effectiveUserId);

          const entryPayload = {
            user_id: effectiveUserId,
            company_id: resolvedCompanyId,
            quote_id: nullIfOffline(entry?.quote_id),
            client_id: nullIfOffline(entry?.client_id),
            category_id: nullIfOffline(entry?.category_id),
            recurrence_parent_id: nullIfOffline(entry?.recurrence_parent_id),
            kind: entry?.kind === 'expense' ? 'expense' : 'income',
            description: String(entry?.description ?? '').trim(),
            amount: Number(entry?.amount ?? 0),
            due_date: entry?.due_date,
            paid_date: entry?.paid_date ?? null,
            status: entry?.status || 'pending',
            payment_method: entry?.payment_method ?? null,
            recurrence: entry?.recurrence ?? null,
            notes: entry?.notes ?? null,
            // Colunas do grupo 5. `billable` NUNCA chega aqui como true:
            // vincular despesa cobrável é online-only (a fila mapeia
            // 1 operação → 1 uuid e não teria como criar a linha do orçamento
            // nem recalcular o total). Normalizado para false de propósito —
            // deixar passar violaria o CHECK do banco e travaria a fila inteira.
            billable: false,
            // O comprovante anexado SEM rede é um dataURL; aqui ele sobe para o
            // bucket privado e vira URL de objeto. Sem esta conversão o dataURL
            // inteiro iria para a coluna `text` do banco.
            attachment_url: await ensureStorageUrl(
              entry?.attachment_url ?? null,
              FINANCE_ATTACHMENT_BUCKET,
              FINANCE_ATTACHMENT_FOLDER,
              effectiveUserId,
            ),
          };

          const { data: created, error } = await supabase
            .from('finance_entries')
            .insert(entryPayload)
            .select('*')
            .single();

          if (error) throw error;

          // Replace temp entry in cache with the real saved row.
          const cached = (getCachedFinanceEntries(effectiveUserId) || []) as any[];
          const filtered = cached.filter((e: any) => e.id !== operation.id && e.id !== created.id);
          filtered.unshift({ ...(created as any), _offline_pending: false });
          cacheFinanceEntries(effectiveUserId, filtered);

          registerOfflineIdMapping(operation.id, (created as any).id);

          return true;
        }

        case 'update_finance_entry': {
          const { entryId, updates, userId } = operation.data;

          const nullIfOffline = (val: any): string | null => {
            if (!val) return null;
            const str = String(val);
            return str.startsWith('offline_') ? null : str;
          };

          const updatePayload: Record<string, any> = {};
          const assign = (key: string, value: any) => {
            if (value !== undefined) updatePayload[key] = value;
          };
          assign('company_id', updates?.company_id !== undefined ? nullIfOffline(updates.company_id) : undefined);
          assign('quote_id', updates?.quote_id !== undefined ? nullIfOffline(updates.quote_id) : undefined);
          assign('client_id', updates?.client_id !== undefined ? nullIfOffline(updates.client_id) : undefined);
          assign('category_id', updates?.category_id !== undefined ? nullIfOffline(updates.category_id) : undefined);
          assign('kind', updates?.kind);
          assign('description', updates?.description);
          assign('amount', updates?.amount !== undefined ? Number(updates.amount) : undefined);
          assign('due_date', updates?.due_date);
          assign('paid_date', updates?.paid_date);
          assign('status', updates?.status);
          assign('payment_method', updates?.payment_method);
          assign('recurrence', updates?.recurrence);
          assign('notes', updates?.notes);
          // Colunas do grupo 5. `billable` fica FORA do update offline de
          // propósito: mudar a marcação implica criar/remover a linha do
          // orçamento e regravar `quotes.total`, coisa que a fila não faz.
          assign(
            'attachment_url',
            updates?.attachment_url !== undefined
              // Mesmo motivo do create: offline o comprovante é um dataURL.
              ? await ensureStorageUrl(updates.attachment_url, FINANCE_ATTACHMENT_BUCKET, FINANCE_ATTACHMENT_FOLDER, userId)
              : undefined,
          );

          const { error } = await supabase
            .from('finance_entries')
            .update(updatePayload)
            .eq('id', entryId)
            .eq('user_id', userId);

          if (error) throw error;

          if (userId) {
            const cached = (getCachedFinanceEntries(userId) || []) as any[];
            const idx = cached.findIndex((e: any) => e.id === entryId);
            if (idx !== -1) {
              cached[idx] = { ...cached[idx], ...updatePayload, _offline_pending: false };
              cacheFinanceEntries(userId, cached);
            }
          }

          return true;
        }

        case 'delete_finance_entry': {
          const { entryId, userId } = operation.data;

          const { error } = await supabase
            .from('finance_entries')
            .delete()
            .eq('id', entryId)
            .eq('user_id', userId);

          if (error) throw error;

          const cached = (getCachedFinanceEntries(userId) || []) as any[];
          cacheFinanceEntries(userId, cached.filter((e: any) => e.id !== entryId));

          return true;
        }

        case 'create_service_order': {
          const { serviceOrder, items, userId, companyId } = operation.data;

          const { data: authData } = await supabase.auth.getUser();
          const effectiveUserId = authData.user?.id || userId;

          const nullIfOffline = (val: any): string | null => {
            if (!val) return null;
            const str = String(val);
            return str.startsWith('offline_') ? null : str;
          };

          // Team-account RLS rejects a SO whose company the user can't write to
          // (null included), so resolve to the carried company or a writable one.
          const resolvedCompanyId = await resolveSyncCompanyId(
            nullIfOffline(companyId ?? serviceOrder?.company_id),
            effectiveUserId,
          );
          if (!resolvedCompanyId) {
            console.error('[NetworkContext] Service order sync aborted: missing company_id');
            if ((operation as any)?.retries === 0) {
              toast.error('Não foi possível sincronizar a ordem de serviço: selecione/crie uma empresa e tente novamente.');
            }
            return false;
          }

          // Next per-company sequence: max existing SO sequence_id + 1.
          const { data: maxSeqRow } = await supabase
            .from('service_orders')
            .select('sequence_id')
            .eq('company_id', resolvedCompanyId)
            .not('sequence_id', 'is', null)
            .order('sequence_id', { ascending: false })
            .limit(1)
            .maybeSingle();
          const nextSequenceId = ((maxSeqRow as any)?.sequence_id ?? 0) + 1;

          const orderPayload = {
            user_id: effectiveUserId,
            company_id: resolvedCompanyId,
            quote_id: nullIfOffline(serviceOrder?.quote_id),
            client_id: nullIfOffline(serviceOrder?.client_id),
            client_name: serviceOrder?.client_name || null,
            client_phone: serviceOrder?.client_phone || null,
            client_address: serviceOrder?.client_address || null,
            client_document: serviceOrder?.client_document || null,
            title: serviceOrder?.title || null,
            description: serviceOrder?.description || null,
            status: serviceOrder?.status || 'pending',
            scheduled_date: serviceOrder?.scheduled_date ?? null,
            completed_at: serviceOrder?.completed_at ?? null,
            show_sequence_id: serviceOrder?.show_sequence_id || false,
            // Dinheiro pela MESMA função do orçamento, recalculado a partir dos
            // itens que a fila carrega (#69). Confiar no `total` serializado
            // deixaria a OS sincronizada com o número de um bundle antigo.
            ...buildQuoteTotalsColumns({
              items: (Array.isArray(items) ? items : []) as never,
              discount: serviceOrder?.discount,
              discountType: serviceOrder?.discount_type,
              shipping: serviceOrder?.shipping,
            }),
            // Diagnóstico técnico: sem esta linha, uma OS criada SEM REDE
            // sincroniza com os três campos em branco — o toast é de sucesso e
            // o texto some (mesmo modo de falha do #62 no executor offline).
            ...toDiagnosisColumns(serviceOrder),
            // Equipamento (5) e cabeçalho (9) pelo mesmo motivo, e com o mesmo
            // sintoma se faltarem: sincroniza em branco, sem erro nenhum.
            ...toAssetColumns(serviceOrder),
            ...buildServiceOrderHeaderColumns(serviceOrder),
            // A assinatura coletada offline na entrega precisa subir junto — ela
            // é a prova de que o cliente recebeu o serviço.
            ...buildServiceOrderSignatureColumns({
              signature: serviceOrder?.client_signature,
              signerName: serviceOrder?.client_signer_name,
              signedAt: serviceOrder?.client_signed_at,
            }),
            sequence_id: nextSequenceId,
          };

          const { data: newOrder, error: orderError } = await supabase
            .from('service_orders')
            .insert(orderPayload)
            .select('*')
            .single();
          if (orderError) throw orderError;

          // Items
          if (Array.isArray(items) && items.length > 0) {
            const itemsToInsert: Record<string, any>[] = [];
            for (let i = 0; i < items.length; i++) {
              itemsToInsert.push(await toDbServiceOrderItem(items[i], newOrder.id, i, effectiveUserId));
            }
            const { error: itemsError } = await supabase.from('service_order_items').insert(itemsToInsert as ServiceOrderItemInsert[]);
            if (itemsError) {
              // Best-effort rollback to avoid duplicates on retry.
              await supabase.from('service_order_items').delete().eq('service_order_id', newOrder.id);
              await supabase.from('service_orders').delete().eq('id', newOrder.id);
              throw itemsError;
            }
          }

          // Photos
          const photos = operation.data.photos || [];
          if (Array.isArray(photos) && photos.length > 0) {
            const photosToInsert: Record<string, any>[] = [];
            for (let i = 0; i < photos.length; i++) {
              const row = await toDbServiceOrderPhoto(photos[i], newOrder.id, effectiveUserId, i);
              if (row) photosToInsert.push(row);
            }
            if (photosToInsert.length > 0) {
              const { error: photosError } = await supabase.from('service_order_photos').insert(photosToInsert as ServiceOrderPhotoInsert[]);
              if (photosError) {
                await supabase.from('service_order_items').delete().eq('service_order_id', newOrder.id);
                await supabase.from('service_order_photos').delete().eq('service_order_id', newOrder.id);
                await supabase.from('service_orders').delete().eq('id', newOrder.id);
                throw photosError;
              }
            }
          }

          // Replace temp SO in cache with the real saved row.
          const cachedOrders = (getCachedServiceOrders(effectiveUserId) || []) as any[];
          const filtered = cachedOrders.filter((o: any) => o.id !== operation.id && o.id !== newOrder.id);
          filtered.unshift({
            ...(newOrder as any),
            _offline_pending: false,
            _offline_items: items || [],
            _offline_photos: operation.data.photos || [],
          });
          cacheServiceOrders(effectiveUserId, filtered);

          registerOfflineIdMapping(operation.id, newOrder.id);

          return true;
        }

        case 'update_service_order': {
          const { serviceOrderId, updates, userId } = operation.data;

          const nullIfOffline = (val: any): string | null => {
            if (!val) return null;
            const str = String(val);
            return str.startsWith('offline_') ? null : str;
          };

          const updatePayload: Record<string, any> = {};
          const assign = (key: string, value: any) => {
            if (value !== undefined) updatePayload[key] = value;
          };
          assign('quote_id', updates?.quote_id !== undefined ? nullIfOffline(updates.quote_id) : undefined);
          assign('client_id', updates?.client_id !== undefined ? nullIfOffline(updates.client_id) : undefined);
          assign('client_name', updates?.client_name);
          assign('client_phone', updates?.client_phone);
          assign('client_address', updates?.client_address);
          assign('client_document', updates?.client_document);
          assign('title', updates?.title);
          assign('description', updates?.description);
          assign('status', updates?.status);
          assign('scheduled_date', updates?.scheduled_date);
          assign('completed_at', updates?.completed_at);
          // Diagnóstico técnico. Este é o QUARTO payload de `service_orders`
          // montado por lista explícita, e o mais fácil de esquecer: sem estas
          // três linhas a edição OFFLINE some ao sincronizar — a fila carrega
          // os campos, o `update` sobe sem eles e o toast é de sucesso.
          // Foi assim que o bug apareceu no device, e não no `tsc` (#33/#62).
          assign('defect', updates?.defect);
          assign('diagnosis', updates?.diagnosis);
          assign('solution', updates?.solution);
          // Equipamento, cabeçalho e assinatura: por LISTA DE NOMES, não por 17
          // `assign` escritos à mão. O update é o payload mais fácil de deixar
          // para trás (#89) e o único jeito de ele não envelhecer é derivar as
          // colunas do mesmo lugar que as grava.
          for (const column of [
            ...ASSET_COLUMNS,
            ...SERVICE_ORDER_HEADER_COLUMNS,
            'client_signature',
            'client_signer_name',
            'client_signed_at',
          ]) {
            assign(column, (updates as Record<string, unknown> | undefined)?.[column]);
          }
          // Dinheiro: as três colunas de ajuste viajam cruas (o total já vem
          // calculado pela tela, que é quem tem os itens em mãos).
          assign('discount', updates?.discount !== undefined ? Number(updates.discount) : undefined);
          assign('discount_type', updates?.discount_type);
          assign('shipping', updates?.shipping !== undefined ? Number(updates.shipping) : undefined);
          assign('total', updates?.total !== undefined ? Number(updates.total) : undefined);

          const { error } = await supabase
            .from('service_orders')
            .update(updatePayload)
            .eq('id', serviceOrderId)
            .eq('user_id', userId);
          if (error) throw error;

          if (userId) {
            const cached = (getCachedServiceOrders(userId) || []) as any[];
            const idx = cached.findIndex((o: any) => o.id === serviceOrderId);
            if (idx !== -1) {
              cached[idx] = { ...cached[idx], ...updatePayload, _offline_pending: false };
              cacheServiceOrders(userId, cached);
            }
          }

          return true;
        }

        case 'delete_service_order': {
          const { serviceOrderId, userId, companyId, returnStock } = operation.data;

          // Mesma regra do `delete_quote`: calcula AQUI (antes do delete,
          // porque `stock_movements.service_order_id` é SET NULL) e aplica
          // depois de o documento ter morrido de fato.
          await runDocumentDeletion({
            prepareReturn: () =>
              returnStock === true && companyId
                ? prepareDocumentStockReturn(String(companyId), 'service_order', [
                    String(serviceOrderId),
                  ])
                : Promise.resolve(null),
            remove: async () => {
              const { error } = await supabase
                .from('service_orders')
                .delete()
                .eq('id', serviceOrderId)
                .eq('user_id', userId);
              if (error) throw error;
            },
          });

          const cached = (getCachedServiceOrders(userId) || []) as any[];
          cacheServiceOrders(userId, cached.filter((o: any) => o.id !== serviceOrderId));

          return true;
        }

        case 'create_appointment': {
          const { appointment, userId, companyId } = operation.data;

          const { data: authData } = await supabase.auth.getUser();
          const effectiveUserId = authData.user?.id || userId;

          const nullIfOffline = (val: any): string | null => {
            if (!val) return null;
            const str = String(val);
            return str.startsWith('offline_') ? null : str;
          };

          // Team-account RLS rejects an appointment whose company the user can't
          // write to (null included), so resolve to the carried company or a
          // writable one.
          const resolvedCompanyId = await resolveSyncCompanyId(
            nullIfOffline(companyId ?? appointment?.company_id),
            effectiveUserId,
          );
          if (!resolvedCompanyId) {
            console.error('[NetworkContext] Appointment sync aborted: missing company_id');
            if ((operation as any)?.retries === 0) {
              toast.error('Não foi possível sincronizar o compromisso: selecione/crie uma empresa e tente novamente.');
            }
            return false;
          }

          const appointmentPayload = {
            user_id: effectiveUserId,
            company_id: resolvedCompanyId,
            client_id: nullIfOffline(appointment?.client_id),
            client_name: appointment?.client_name ?? null,
            service_order_id: nullIfOffline(appointment?.service_order_id),
            quote_id: nullIfOffline(appointment?.quote_id),
            title: appointment?.title ?? null,
            description: appointment?.description ?? null,
            location: appointment?.location ?? null,
            starts_at: appointment?.starts_at,
            ends_at: appointment?.ends_at ?? null,
            all_day: appointment?.all_day ?? false,
            status: appointment?.status || 'scheduled',
            reminder_minutes: appointment?.reminder_minutes ?? null,
          };

          const { data: created, error } = await supabase
            .from('appointments')
            .insert(appointmentPayload)
            .select('*')
            .single();
          if (error) throw error;

          // Replace temp appointment in cache with the real saved row.
          const cached = (getCachedAppointments(effectiveUserId) || []) as any[];
          const filtered = cached.filter((a: any) => a.id !== operation.id && a.id !== created.id);
          filtered.unshift({ ...(created as any), _offline_pending: false });
          cacheAppointments(effectiveUserId, filtered);

          registerOfflineIdMapping(operation.id, (created as any).id);

          return true;
        }

        case 'update_appointment': {
          const { appointmentId, updates, userId } = operation.data;

          const nullIfOffline = (val: any): string | null => {
            if (!val) return null;
            const str = String(val);
            return str.startsWith('offline_') ? null : str;
          };

          const updatePayload: Record<string, any> = {};
          const assign = (key: string, value: any) => {
            if (value !== undefined) updatePayload[key] = value;
          };
          assign('client_id', updates?.client_id !== undefined ? nullIfOffline(updates.client_id) : undefined);
          assign('client_name', updates?.client_name);
          assign('service_order_id', updates?.service_order_id !== undefined ? nullIfOffline(updates.service_order_id) : undefined);
          assign('quote_id', updates?.quote_id !== undefined ? nullIfOffline(updates.quote_id) : undefined);
          assign('title', updates?.title);
          assign('description', updates?.description);
          assign('location', updates?.location);
          assign('starts_at', updates?.starts_at);
          assign('ends_at', updates?.ends_at);
          assign('all_day', updates?.all_day);
          assign('status', updates?.status);
          assign('reminder_minutes', updates?.reminder_minutes);

          const { error } = await supabase
            .from('appointments')
            .update(updatePayload)
            .eq('id', appointmentId)
            .eq('user_id', userId);
          if (error) throw error;

          if (userId) {
            const cached = (getCachedAppointments(userId) || []) as any[];
            const idx = cached.findIndex((a: any) => a.id === appointmentId);
            if (idx !== -1) {
              cached[idx] = { ...cached[idx], ...updatePayload, _offline_pending: false };
              cacheAppointments(userId, cached);
            }
          }

          return true;
        }

        case 'delete_appointment': {
          const { appointmentId, userId } = operation.data;

          const { error } = await supabase
            .from('appointments')
            .delete()
            .eq('id', appointmentId)
            .eq('user_id', userId);
          if (error) throw error;

          const cached = (getCachedAppointments(userId) || []) as any[];
          cacheAppointments(userId, cached.filter((a: any) => a.id !== appointmentId));

          return true;
        }

        case 'create_contract': {
          const { contract, userId, companyId } = operation.data;

          const { data: authData } = await supabase.auth.getUser();
          const effectiveUserId = authData.user?.id || userId;

          const nullIfOffline = (val: any): string | null => {
            if (!val) return null;
            const str = String(val);
            return str.startsWith('offline_') ? null : str;
          };

          // Team-account RLS rejects a contract whose company the user can't write
          // to (null included), so resolve to the carried company or a writable one.
          const resolvedCompanyId = await resolveSyncCompanyId(
            nullIfOffline(companyId ?? contract?.company_id),
            effectiveUserId,
          );
          if (!resolvedCompanyId) {
            console.error('[NetworkContext] Contract sync aborted: missing company_id');
            if ((operation as any)?.retries === 0) {
              toast.error('Não foi possível sincronizar o contrato: selecione/crie uma empresa e tente novamente.');
            }
            return false;
          }

          // Next per-company sequence: max existing contract sequence_id + 1.
          const { data: maxSeqRow } = await supabase
            .from('contracts')
            .select('sequence_id')
            .eq('company_id', resolvedCompanyId)
            .not('sequence_id', 'is', null)
            .order('sequence_id', { ascending: false })
            .limit(1)
            .maybeSingle();
          const nextSequenceId = ((maxSeqRow as any)?.sequence_id ?? 0) + 1;

          // Only the DRAFT is synced offline — signing/sending are online, so we
          // never write signature/token columns here.
          const contractPayload = {
            user_id: effectiveUserId,
            company_id: resolvedCompanyId,
            quote_id: nullIfOffline(contract?.quote_id),
            client_id: nullIfOffline(contract?.client_id),
            client_name: contract?.client_name || null,
            client_phone: contract?.client_phone || null,
            client_address: contract?.client_address || null,
            client_document: contract?.client_document || null,
            title: contract?.title || null,
            service_description: contract?.service_description || null,
            content: contract?.content || null,
            status: contract?.status || 'draft',
            total: Number(contract?.total ?? 0),
            show_sequence_id: contract?.show_sequence_id || false,
            sequence_id: nextSequenceId,
            // Cláusulas: ITERADAS da lista canônica. Onze `assign()` à mão aqui
            // seriam onze chances de a próxima coluna ficar para trás — que é
            // exatamente como o #89 nasceu na OS. `?? null` e não `|| null`
            // porque `0` é um percentual legítimo (#61).
            ...Object.fromEntries(
              CONTRACT_CLAUSE_COLUMNS.map(({ column }) => [column, contract?.[column] ?? null]),
            ),
          };

          const { data: created, error } = await supabase
            .from('contracts')
            .insert(contractPayload)
            .select('*')
            .single();
          if (error) throw error;

          // Replace temp contract in cache with the real saved row.
          const cached = (getCachedContracts(effectiveUserId) || []) as any[];
          const filtered = cached.filter((c: any) => c.id !== operation.id && c.id !== created.id);
          filtered.unshift({ ...(created as any), _offline_pending: false });
          cacheContracts(effectiveUserId, filtered);

          registerOfflineIdMapping(operation.id, (created as any).id);

          return true;
        }

        case 'update_contract': {
          const { contractId, updates, userId } = operation.data;

          const nullIfOffline = (val: any): string | null => {
            if (!val) return null;
            const str = String(val);
            return str.startsWith('offline_') ? null : str;
          };

          const updatePayload: Record<string, any> = {};
          const assign = (key: string, value: any) => {
            if (value !== undefined) updatePayload[key] = value;
          };
          assign('quote_id', updates?.quote_id !== undefined ? nullIfOffline(updates.quote_id) : undefined);
          assign('client_id', updates?.client_id !== undefined ? nullIfOffline(updates.client_id) : undefined);
          assign('client_name', updates?.client_name);
          assign('client_phone', updates?.client_phone);
          assign('client_address', updates?.client_address);
          assign('client_document', updates?.client_document);
          assign('title', updates?.title);
          assign('service_description', updates?.service_description);
          assign('content', updates?.content);
          assign('status', updates?.status);
          assign('total', updates?.total !== undefined ? Number(updates.total) : undefined);
          // Mesma lista canônica do create — os dois divergiriam na primeira
          // coluna nova se cada um mantivesse a sua (#89/#219).
          for (const { column } of CONTRACT_CLAUSE_COLUMNS) {
            assign(column, updates?.[column]);
          }

          const { error } = await supabase
            .from('contracts')
            .update(updatePayload)
            .eq('id', contractId)
            .eq('user_id', userId);
          if (error) throw error;

          if (userId) {
            const cached = (getCachedContracts(userId) || []) as any[];
            const idx = cached.findIndex((c: any) => c.id === contractId);
            if (idx !== -1) {
              cached[idx] = { ...cached[idx], ...updatePayload, _offline_pending: false };
              cacheContracts(userId, cached);
            }
          }

          return true;
        }

        case 'delete_contract': {
          const { contractId, userId } = operation.data;

          const { error } = await supabase
            .from('contracts')
            .delete()
            .eq('id', contractId)
            .eq('user_id', userId);
          if (error) throw error;

          const cached = (getCachedContracts(userId) || []) as any[];
          cacheContracts(userId, cached.filter((c: any) => c.id !== contractId));

          return true;
        }

        case 'create_receipt': {
          const { receipt, userId, companyId } = operation.data;

          const { data: authData } = await supabase.auth.getUser();
          const effectiveUserId = authData.user?.id || userId;

          const nullIfOffline = (val: any): string | null => {
            if (!val) return null;
            const str = String(val);
            return str.startsWith('offline_') ? null : str;
          };

          // Team-account RLS rejects a receipt whose company the user can't write
          // to (null included), so resolve to the carried company or a writable one.
          const resolvedCompanyId = await resolveSyncCompanyId(
            nullIfOffline(companyId ?? receipt?.company_id),
            effectiveUserId,
          );
          if (!resolvedCompanyId) {
            console.error('[NetworkContext] Receipt sync aborted: missing company_id');
            if ((operation as any)?.retries === 0) {
              toast.error('Não foi possível sincronizar o recibo: selecione/crie uma empresa e tente novamente.');
            }
            return false;
          }

          // Next per-company sequence: max existing receipt sequence_id + 1.
          const { data: maxSeqRow } = await supabase
            .from('receipts')
            .select('sequence_id')
            .eq('company_id', resolvedCompanyId)
            .not('sequence_id', 'is', null)
            .order('sequence_id', { ascending: false })
            .limit(1)
            .maybeSingle();
          const nextSequenceId = ((maxSeqRow as any)?.sequence_id ?? 0) + 1;

          const receiptPayload = {
            user_id: effectiveUserId,
            company_id: resolvedCompanyId,
            quote_id: nullIfOffline(receipt?.quote_id),
            service_order_id: nullIfOffline(receipt?.service_order_id),
            client_id: nullIfOffline(receipt?.client_id),
            finance_entry_id: nullIfOffline(receipt?.finance_entry_id),
            payer_name: receipt?.payer_name || null,
            payer_document: receipt?.payer_document || null,
            payer_address: receipt?.payer_address || null,
            amount: Number(receipt?.amount ?? 0),
            description: receipt?.description || null,
            payment_method: receipt?.payment_method || null,
            // Formas aceitas / condicoes: `null` (legado) e `[]` (o usuario
            // desmarcou tudo) sao coisas diferentes e precisam sobreviver (#73).
            payment_methods: Array.isArray(receipt?.payment_methods)
              ? receipt.payment_methods
              : null,
            payment_terms: receipt?.payment_terms || null,
            // Recibo emitido SEM REDE depois das 21h ganharia a data de amanhã:
            // a data de emissão é do calendário local (#114/D21).
            issue_date: receipt?.issue_date || todayIsoDate(),
            issue_place: receipt?.issue_place || null,
            issuer_signature: receipt?.issuer_signature || null,
            issuer_signer_name: receipt?.issuer_signer_name || null,
            notes: receipt?.notes || null,
            show_sequence_id: receipt?.show_sequence_id || false,
            cancelled_at: receipt?.cancelled_at || null,
            sequence_id: nextSequenceId,
          };

          const { data: created, error } = await supabase
            .from('receipts')
            .insert(receiptPayload)
            .select('*')
            .single();
          if (error) throw error;

          // AS LINHAS DO RECIBO. Elas viajam dentro da operacao (`_items`) e
          // so podem ser inseridas AGORA, porque `receipt_id` e NOT NULL e o
          // id real acabou de nascer. Sem este bloco, um recibo criado sem
          // rede subiria com o valor certo e NENHUMA linha — sem erro nenhum.
          const queuedItems = Array.isArray(receipt?._items) ? receipt._items : [];
          if (queuedItems.length > 0) {
            const { error: itemsError } = await supabase.from('receipt_items').insert(
              queuedItems.map((item: any, index: number) => ({
                ...buildReceiptItemRow(item, index),
                receipt_id: (created as any).id,
              })),
            );
            if (itemsError) throw itemsError;
          }

          // Replace temp receipt in cache with the real saved row.
          const cached = (getCachedReceipts(effectiveUserId) || []) as any[];
          const filtered = cached.filter((r: any) => r.id !== operation.id && r.id !== created.id);
          filtered.unshift({ ...(created as any), _offline_pending: false });
          cacheReceipts(effectiveUserId, filtered);

          registerOfflineIdMapping(operation.id, (created as any).id);

          return true;
        }

        case 'update_receipt': {
          const { receiptId, updates, userId } = operation.data;

          const nullIfOffline = (val: any): string | null => {
            if (!val) return null;
            const str = String(val);
            return str.startsWith('offline_') ? null : str;
          };

          const updatePayload: Record<string, any> = {};
          const assign = (key: string, value: any) => {
            if (value !== undefined) updatePayload[key] = value;
          };
          assign('quote_id', updates?.quote_id !== undefined ? nullIfOffline(updates.quote_id) : undefined);
          assign('service_order_id', updates?.service_order_id !== undefined ? nullIfOffline(updates.service_order_id) : undefined);
          assign('client_id', updates?.client_id !== undefined ? nullIfOffline(updates.client_id) : undefined);
          assign('finance_entry_id', updates?.finance_entry_id !== undefined ? nullIfOffline(updates.finance_entry_id) : undefined);
          assign('payer_name', updates?.payer_name);
          assign('payer_document', updates?.payer_document);
          assign('payer_address', updates?.payer_address);
          assign('amount', updates?.amount !== undefined ? Number(updates.amount) : undefined);
          assign('description', updates?.description);
          assign('payment_method', updates?.payment_method);
          assign('issue_date', updates?.issue_date);
          assign('issue_place', updates?.issue_place);
          assign('issuer_signature', updates?.issuer_signature);
          assign('issuer_signer_name', updates?.issuer_signer_name);
          assign('notes', updates?.notes);
          assign('show_sequence_id', updates?.show_sequence_id);
          assign('cancelled_at', updates?.cancelled_at);
          assign('payment_methods', updates?.payment_methods);
          assign('payment_terms', updates?.payment_terms);

          const { error } = await supabase
            .from('receipts')
            .update(updatePayload)
            .eq('id', receiptId)
            .eq('user_id', userId);
          if (error) throw error;

          // AS LINHAS: `_items` ausente significa "nao mexa" — e essa
          // distincao e o que impede uma edicao offline de QUALQUER campo
          // (o nome do pagador, por exemplo) de apagar os itens do recibo.
          if (Array.isArray(updates?._items)) {
            const { error: delError } = await supabase
              .from('receipt_items')
              .delete()
              .eq('receipt_id', receiptId);
            if (delError) throw delError;

            if (updates._items.length > 0) {
              const { error: insError } = await supabase.from('receipt_items').insert(
                updates._items.map((item: any, index: number) => ({
                  ...buildReceiptItemRow(item, index),
                  receipt_id: receiptId,
                })),
              );
              if (insError) throw insError;
            }
          }

          if (userId) {
            const cached = (getCachedReceipts(userId) || []) as any[];
            const idx = cached.findIndex((r: any) => r.id === receiptId);
            if (idx !== -1) {
              cached[idx] = { ...cached[idx], ...updatePayload, _offline_pending: false };
              cacheReceipts(userId, cached);
            }
          }

          return true;
        }

        case 'delete_receipt': {
          const { receiptId, userId } = operation.data;

          const { error } = await supabase
            .from('receipts')
            .delete()
            .eq('id', receiptId)
            .eq('user_id', userId);
          if (error) throw error;

          const cached = (getCachedReceipts(userId) || []) as any[];
          cacheReceipts(userId, cached.filter((r: any) => r.id !== receiptId));

          return true;
        }

        case 'create_stock_movement': {
          // A operação enfileirada é O MOVIMENTO (delta+motivo+refs), não o saldo.
          // O worker chama a RPC, que RECOMPUTA a partir do saldo real do servidor
          // — dois movimentos offline somam certo. O cliente não é autoritativo.
          const { movement } = operation.data;

          const nullIfOffline = (val: any): string | null => {
            if (!val) return null;
            const str = String(val);
            return str.startsWith('offline_') ? null : str;
          };

          // product_id já deve ter sido resolvido por translateOfflineIds (o
          // create do produto roda antes). Se ainda for offline_, não há como
          // aplicar — falha e será descartado (o chain-cleanup do produto
          // descartado já remove este movimento).
          const productId = nullIfOffline(movement?.product_id);
          if (!productId) {
            console.error('[NetworkContext] Stock movement sync aborted: product_id não resolvido');
            return false;
          }

          // Idempotência da fila para baixas ligadas a documento. Se a RPC
          // respondeu no servidor mas a resposta se perdeu, a operação pode ser
          // tentada novamente; sem este guard o mesmo item seria baixado duas
          // vezes. O guard é por origem + produto, preservando variações.
          const quoteId = nullIfOffline(movement?.quote_id);
          const serviceOrderId = nullIfOffline(movement?.service_order_id);
          const isDocumentSale =
            movement?.reason === 'quote_sale' || movement?.reason === 'service_order_sale';
          if (isDocumentSale && (quoteId || serviceOrderId)) {
            let guard = supabase
              .from('stock_movements')
              .select('id', { count: 'exact', head: true })
              .eq('product_id', productId)
              .eq('reason', movement.reason);
            guard = quoteId
              ? guard.eq('quote_id', quoteId)
              : guard.eq('service_order_id', serviceOrderId!);
            const { count, error: guardError } = await guard;
            if (guardError) throw guardError;
            if ((count ?? 0) > 0) return true;
          }

          const { error } = await supabase.rpc('apply_stock_movement', {
            p_product_id: productId,
            p_delta: Number(movement?.delta ?? 0),
            p_reason: movement?.reason,
            p_quote_id: quoteId,
            p_service_order_id: serviceOrderId,
            p_note: movement?.note ?? null,
          });
          if (error) throw error;

          return true;
        }

        default:
          console.warn('[NetworkContext] Unknown operation type:', operation.type);
          return false;
      }
    } catch (error) {
      console.error('[NetworkContext] Error syncing operation:', operation.id, error);
      Sentry.captureException(error, {
        extra: {
          operation_type: operation.type,
          operation_id: operation.id,
        },
      });
      return false;
    }
  };

  // Sync all pending operations
  const syncNow = useCallback(async () => {
    const online = await checkNetworkStatus();
    if (!online) return;

    // Hard lock to prevent concurrent sync loops
    if (syncLockRef.current) return;
    syncLockRef.current = true;

    try {
      if (retryTimerRef.current) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }

      const priority: Record<string, number> = {
        // Creates first
        create_client: 10,
        create_catalog_item: 20,
        create_company: 30,
        // Quotes after dependencies
        create_quote: 40,
        // Service orders depend on clients/companies/quotes — create after the
        // quote so temp FKs (quote_id/client_id) are already mapped.
        create_service_order: 45,
        // Contracts may reference an offline quote/client — create after the
        // quote so temp FKs (quote_id/client_id) are already mapped.
        create_contract: 46,
        // Receipts may reference an offline quote/OS/client/finance entry —
        // create after those so temp FKs are already mapped.
        create_receipt: 47,
        // Appointments may reference an offline OS/quote/client — create after
        // those so temp FKs are already mapped.
        create_appointment: 48,
        // Stock movements reference a product (prio 20) and optionally a quote
        // (40) / service order (45) — run after those so temp FKs are mapped.
        create_stock_movement: 49,
        update_quote: 50,
        update_service_order: 55,
        update_appointment: 58,
        update_contract: 59,
        update_receipt: 61,
        update_profile: 60,
        // Finance entries depend on clients/companies/quotes (create first,
        // then updates) so temp FKs are already mapped by the time they run.
        create_finance_entry: 70,
        update_finance_entry: 75,
        // Deletes last
        delete_quote: 90,
        delete_client: 91,
        delete_catalog_item: 92,
        delete_company: 93,
        delete_finance_entry: 94,
        delete_service_order: 95,
        delete_appointment: 96,
        delete_contract: 97,
        delete_receipt: 98,
        clear_data: 99,
      };

      const queue = getOfflineQueue().slice().sort((a, b) => {
        const pa = priority[a.type] ?? 500;
        const pb = priority[b.type] ?? 500;
        return pa - pb;
      });
      if (queue.length === 0) return;

      setIsSyncing(true);
      let successCount = 0;
      let failCount = 0;
      const failedOperationLabels: string[] = [];

      // Ops removed mid-loop by chained-cleanup (e.g. a create fails MAX
      // times and its trailing update/delete gets pruned). We skip them
      // instead of firing an already-orphaned request.
      const skipIds = new Set<string>();

      for (const operation of queue) {
        if (skipIds.has(operation.id)) continue;

        // Resolve any offline_ ids in the payload to their real UUIDs (a
        // preceding create in this pass may have registered a mapping). The
        // original op stays in the queue with its offline_ ids so a later
        // retry / cache reconciliation still finds it.
        const translated = translateOfflineIds(operation);
        const success = await syncOperation(translated);

        if (success) {
          removeFromOfflineQueue(operation.id);
          successCount++;
        } else {
          incrementOfflineOperationRetry(operation.id);

          // Remove if too many retries
          if (operation.retries + 1 >= MAX_RETRIES) {
            // If this was a create op, its offline_ id will never map to a
            // real UUID — drop any lingering updates/deletes so they don't
            // explode with "invalid input syntax for type uuid".
            if (
              operation.type === 'create_quote' ||
              operation.type === 'create_client' ||
              operation.type === 'create_catalog_item' ||
              operation.type === 'create_company' ||
              operation.type === 'create_finance_entry' ||
              operation.type === 'create_service_order' ||
              operation.type === 'create_appointment' ||
              operation.type === 'create_contract' ||
              operation.type === 'create_receipt'
            ) {
              const queueBefore = getOfflineQueue();
              removeChainedOfflineOperations(operation.id);
              const queueAfter = getOfflineQueue();
              for (const before of queueBefore) {
                if (!queueAfter.find((op) => op.id === before.id)) {
                  skipIds.add(before.id);
                }
              }
            }

            removeFromOfflineQueue(operation.id);
            failCount++;
            failedOperationLabels.push(describeOfflineOperation(operation));
          }
        }
      }

      // Notify user
      if (successCount > 0) {
        toast.success(`${successCount} item(s) sincronizado(s) com sucesso!`);
        // Dispatch events to refresh data - reload from server
        window.dispatchEvent(new CustomEvent('quotes-updated'));
        window.dispatchEvent(new CustomEvent('clients-updated'));
        window.dispatchEvent(new CustomEvent('catalog-updated'));
        // Let the agenda refresh and re-reconcile local reminders now that any
        // offline_ appointment ids have become real UUIDs.
        window.dispatchEvent(new CustomEvent('appointments-updated'));
        window.dispatchEvent(new CustomEvent('contracts-updated'));
        window.dispatchEvent(new CustomEvent('receipts-updated'));
        // Estoque: os saldos podem ter mudado (RPC recomputou no servidor).
        window.dispatchEvent(new CustomEvent('stock-updated'));
      }

      if (failCount > 0) {
        toast.error(`Não foi possível sincronizar: ${failedOperationLabels.join(', ')}.`);
      }

      // If something is still pending, retry soon (online remains true so the user doesn't need to toggle).
      if (hasOfflineOperations()) {
        retryTimerRef.current = window.setTimeout(() => {
          syncNow();
        }, 8000);
      }
    } finally {
      setIsSyncing(false);
      updatePendingCount();
      syncLockRef.current = false;
    }
  }, [isSyncing, updatePendingCount]);

  // Auto-sync when coming online - use multiple strategies to ensure sync happens
  useEffect(() => {
    if (!isOnline) return;
    if (!hasOfflineOperations()) return;

    // Strategy 1: Immediate sync attempt after brief delay
    const timer1 = setTimeout(() => {
      syncNow();
    }, 1000);

    // Strategy 2: Retry after a longer delay if still pending
    const timer2 = setTimeout(() => {
      if (hasOfflineOperations()) {
        syncNow();
      }
    }, 5000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [isOnline, syncNow]);

  // Cleanup retry timer on unmount
  useEffect(() => {
    return () => {
      if (retryTimerRef.current) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, []);

  // Update pending count on mount and when queue changes
  useEffect(() => {
    updatePendingCount();
    
    // Listen for queue changes
    const handleQueueChange = () => {
      updatePendingCount();
      if (isOnline && hasOfflineOperations()) {
        syncNow();
      }
    };
    window.addEventListener('offline-queue-updated', handleQueueChange);
    
    return () => window.removeEventListener('offline-queue-updated', handleQueueChange);
  }, [updatePendingCount, isOnline, syncNow]);

  // On mount: restore cached pending quotes whose queue entry was discarded by
  // an older app version, then try a sync once.
  useEffect(() => {
    let cancelled = false;
    let timer: number | null = null;

    void supabase.auth.getUser().then(({ data }) => {
      if (cancelled || !data.user?.id) return;

      const restored = restoreOrphanedCachedQuoteOperations(data.user.id);
      if (restored > 0) {
        updatePendingCount();
      }
      if (!hasOfflineOperations()) return;

      timer = window.setTimeout(() => {
        syncNow();
      }, 1500);
    });

    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [syncNow, updatePendingCount]);

  // Listen directly for browser online event as a fallback (in case useNetworkStatus is slow)
  useEffect(() => {
    const handleOnline = () => {
      console.log('[NetworkContext] Browser online event detected');
      if (hasOfflineOperations()) {
        // Give a small delay for network to stabilize
        setTimeout(() => {
          syncNow();
        }, 1500);
      }
    };

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [syncNow]);

  return (
    <NetworkContext.Provider
      value={{
        isOnline,
        isSyncing,
        pendingOperations,
        syncNow,
      }}
    >
      {children}
    </NetworkContext.Provider>
  );
}

export function useNetwork() {
  const context = useContext(NetworkContext);
  if (context === undefined) {
    throw new Error('useNetwork must be used within a NetworkProvider');
  }
  return context;
}
